import clsx from "clsx";
import React, { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MOBILE_BREAKPOINT } from "react-migration/lib/theme/Theme";

type ModalLayoutProps = {
  onClose?: () => void;
  size?: "small" | "large";
  fullWidth?: boolean;
  fullHeight?: boolean;
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  containerClassName?: string;
  portalParent?: HTMLElement | null;
  children?: React.ReactNode;
};

export const ModalLayout: FC<ModalLayoutProps> = ({
  onClose,
  size = "small",
  fullWidth = false,
  fullHeight = false,
  tabIndex,
  onKeyDown,
  containerClassName,
  portalParent,
  children,
}: ModalLayoutProps) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const parentContainer = portalParent || document.body;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  // Prevent scroll
  useEffect(() => {
    const originalOverflow = parentContainer.style.overflow;
    parentContainer.style.overflow = "hidden";

    if (typeof tabIndex === "number" && modalContentRef.current) {
      modalContentRef.current.focus();
    }

    return () => {
      parentContainer.style.overflow = originalOverflow;
    };
  }, [parentContainer.style, tabIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      onClose?.();
    }
    onKeyDown?.(e);
  };

  const blockEvent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const blockPointerEvents = {
    onMouseOver: blockEvent,
    onPointerOver: blockEvent,
    onMouseMove: blockEvent,
    onPointerMove: blockEvent,
    onPointerDown: blockEvent,
  };

  return createPortal(
    <div
      className="fixed top-0 left-0 flex w-full h-full pointer-events-none bg-neutral-600a z-900"
      {...blockPointerEvents}
      onClick={onClose}
    >
      <div className="flex items-center justify-center w-full h-full">
        <div
          className={clsx(
            "flex flex-col items-start gap-4 justify-start p-4",
            "focus:outline-hidden focus:ring-0 focus-visible:outline-hidden focus-visible:ring-0",
            "rounded-lg bg-white shadow-xl",
            "max-h-[90vh] overflow-hidden w-fit",
            "pointer-events-auto z-901",
            {
              "mt-(--navbar-height)": window.screen.width > MOBILE_BREAKPOINT,
              "mb-(--navbar-mobile-height)": window.screen.width <= MOBILE_BREAKPOINT,
              "md:max-w-[1200px] lg:max-w-[1600px]": size === "large",
              "md:max-w-[800px] lg:max-w-[1200px]": size === "small",
              "w-full": fullWidth,
              "h-full": fullHeight,
              "h-auto": !fullHeight,
            },
            containerClassName
          )}
          ref={modalContentRef}
          onClick={(e) => e.stopPropagation()}
          tabIndex={tabIndex}
          onKeyDown={handleKeyDown}
        >
          {children}
        </div>
      </div>
    </div>,
    parentContainer
  );
};

ModalLayout.displayName = "ModalLayout";
